import React, { useEffect, useState } from 'react'
import { getAPIBaseURL } from '../../services/helpers'
import { getData } from '../../services/request'

const StatCard = ({ label, value, subtitle, icon }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-medium text-gray-600">{label}</p>
        <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
      </div>
      {icon && <div className="text-gray-400">{icon}</div>}
    </div>
  </div>
)

const StatusBreakdown = ({ title, data, colors, total }) => {
  const totalValue = total || Object.values(data || {}).reduce((sum, val) => sum + (val || 0), 0)
  if (!data || Object.keys(data).length === 0) return null
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-gray-900">{title}</h3>
      <div className="space-y-2">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="flex items-center justify-between">
            <span className="text-xs text-gray-600">{key.replace(/_/g, ' ')}</span>
            <div className="flex items-center gap-2">
              <div className="h-2 w-24 rounded-full bg-gray-200">
                <div
                  className="h-2 rounded-full"
                  style={{
                    width: `${totalValue > 0 ? ((value || 0) / totalValue) * 100 : 0}%`,
                    backgroundColor: colors?.[key] || '#6B7280'
                  }}
                />
              </div>
              <span className="text-xs font-semibold text-gray-900">{value ?? 0}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const AdminReports = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [reports, setReports] = useState(null)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const fetchReports = async () => {
    setLoading(true)
    setError('')
    const url = `${getAPIBaseURL()}/employme/admin/reports`
    const params = {}
    if (startDate) params.startDate = startDate
    if (endDate) params.endDate = endDate

    const res = await getData(url, {}, params)
    setLoading(false)

    if (res.error === '') {
      if (res.result?.status === 200) {
        const data = res.result.data?.data || res.result.data
        setReports(data)
      } else {
        setError(res.result?.data?.message || 'Failed to load reports')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  useEffect(() => {
    fetchReports()
  }, [startDate, endDate])

  const handleClearFilter = () => {
    setStartDate('')
    setEndDate('')
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount ?? 0)
  }

  const jobStatusColors = {
    DRAFT: '#9CA3AF',
    PENDING_PAYMENT: '#F59E0B',
    PENDING_APPROVAL: '#EAB308',
    LIVE: '#10B981',
    CLOSED: '#6B7280',
    EXPIRED: '#EF4444'
  }

  const applicationStatusColors = {
    APPLIED: '#F59E0B',
    SUBMITTED: '#3B82F6',
    UNDER_REVIEW: '#8B5CF6',
    SELECTED: '#10B981',
    REJECTED: '#EF4444',
    EXPIRED: '#6B7280'
  }

  const paymentStatusColors = {
    completed: '#10B981',
    initiated: '#3B82F6',
    failed: '#EF4444'
  }

  const companyStatusColors = {
    PENDING_VERIFICATION: '#F59E0B',
    ACTIVE: '#10B981',
    SUSPENDED: '#EF4444'
  }

  return (
    <section className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>

        <div className="flex flex-wrap gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
            placeholder="Start Date"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
            placeholder="End Date"
          />
          {(startDate || endDate) && (
            <button
              onClick={handleClearFilter}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          Loading reports...
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">{error}</div>
      ) : !reports ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          No report data available
        </div>
      ) : (
        <>
          {/* User Statistics */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">User Statistics</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Total Employers"
                value={reports.users?.employers?.total ?? 0}
                subtitle={`${reports.users?.employers?.active ?? 0} active, ${reports.users?.employers?.inactive ?? 0} suspended`}
              />
              <StatCard
                label="Total Job Seekers"
                value={reports.users?.jobSeekers?.total ?? 0}
                subtitle={`${reports.users?.jobSeekers?.active ?? 0} active, ${reports.users?.jobSeekers?.inactive ?? 0} suspended`}
              />
              <StatCard
                label="Total Companies"
                value={reports.companies?.total ?? 0}
                subtitle={`${reports.companies?.ACTIVE ?? 0} active, ${reports.companies?.PENDING_VERIFICATION ?? 0} pending`}
              />
              <StatCard
                label="Total Users"
                value={(reports.users?.employers?.total ?? 0) + (reports.users?.jobSeekers?.total ?? 0)}
                subtitle="Employers + Job Seekers"
              />
            </div>
          </div>

          {/* Job Statistics */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">Job Statistics</h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <StatCard label="Total Jobs" value={reports.jobs?.total ?? 0} />
              <StatusBreakdown
                title="Jobs by Status"
                data={reports.jobs?.byStatus}
                colors={jobStatusColors}
                total={reports.jobs?.total}
              />
            </div>
          </div>

          {/* Application Statistics */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">Application Statistics</h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <StatCard label="Total Applications" value={reports.applications?.total ?? 0} />
              <StatusBreakdown
                title="Applications by Status"
                data={reports.applications?.byStatus}
                colors={applicationStatusColors}
                total={reports.applications?.total}
              />
            </div>
          </div>

          {/* Payment Statistics */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">Payment Statistics (Job Listings)</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Total Payments" value={reports.payments?.total ?? 0} />
              <StatCard label="Completed Payments" value={reports.payments?.byStatus?.completed ?? 0} />
              <StatCard
                label="Total Revenue"
                value={formatCurrency(reports.payments?.revenue?.total)}
                subtitle={reports.payments?.revenue?.currency}
              />
              <StatCard
                label="Average Payment"
                value={formatCurrency(reports.payments?.revenue?.average)}
                subtitle={reports.payments?.revenue?.currency}
              />
            </div>
            <div className="mt-4">
              <StatusBreakdown
                title="Payments by Status"
                data={reports.payments?.byStatus}
                colors={paymentStatusColors}
                total={reports.payments?.total}
              />
            </div>
          </div>

          {/* Companies by Status */}
          {reports.companies && (
            <div>
              <h2 className="mb-3 text-lg font-semibold text-gray-900">Companies by Status</h2>
              <StatusBreakdown
                title="Company verification status"
                data={{
                  PENDING_VERIFICATION: reports.companies.PENDING_VERIFICATION ?? 0,
                  ACTIVE: reports.companies.ACTIVE ?? 0,
                  SUSPENDED: reports.companies.SUSPENDED ?? 0
                }}
                colors={companyStatusColors}
                total={reports.companies.total}
              />
            </div>
          )}

          {/* Recent Activity */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">Recent Activity (Last 7 Days)</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <StatCard
                label="New Employers"
                value={reports.recentActivity?.employers ?? 0}
                subtitle="Last 7 days"
              />
              <StatCard
                label="New Job Seekers"
                value={reports.recentActivity?.jobSeekers ?? 0}
                subtitle="Last 7 days"
              />
              <StatCard
                label="New Companies"
                value={reports.recentActivity?.companies ?? 0}
                subtitle="Last 7 days"
              />
              <StatCard label="New Jobs" value={reports.recentActivity?.jobs ?? 0} subtitle="Last 7 days" />
              <StatCard
                label="New Applications"
                value={reports.recentActivity?.applications ?? 0}
                subtitle="Last 7 days"
              />
            </div>
          </div>

          {(reports.dateRange?.startDate || reports.dateRange?.endDate) && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
              <strong>Date Range:</strong>{' '}
              {reports.dateRange.startDate
                ? new Date(reports.dateRange.startDate).toLocaleDateString()
                : 'Beginning'}{' '}
              to{' '}
              {reports.dateRange.endDate
                ? new Date(reports.dateRange.endDate).toLocaleDateString()
                : 'Today'}
            </div>
          )}
        </>
      )}
    </section>
  )
}

export default AdminReports
